import {inject} from '@angular/core';
import {HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {finalize} from 'rxjs';
import {environment} from '../../../environments/environment';
import {LoadingService} from '../services/loading.service';

/**
 * Tracks the lifecycle of backend HTTP requests so that the global loading
 * indicator can show / hide accordingly. Non-backend requests (assets, etc.)
 * are passed through untouched.
 */
/**
 * Sentinel header set by the realtime polling-fallback path
 * (`RealtimeService.pollHttp`) to opt the request out of the global
 * loading counter. Production fix 2026-06-02: the every-2s polls were
 * keeping the top progress bar stuck on indefinitely once the WS
 * breaker tripped, because the counter could never drain.
 *
 * The header is stripped here before the request is forwarded so the
 * BE never sees it (zero OpenAPI contract impact).
 */
const REALTIME_POLL_HEADER = 'X-Realtime-Poll';

export const loadingInterceptor: HttpInterceptorFn = (request, next) => {
    if (request.headers.has(REALTIME_POLL_HEADER)) {
        const cleaned = request.clone({headers: request.headers.delete(REALTIME_POLL_HEADER)});
        return next(cleaned);
    }

    const loadingService = inject(LoadingService);

    if (!isBackendRequest(request.url)) {
        return next(request);
    }

    const requestId = buildRequestId(request);
    const requestLabel = `${request.method} ${request.urlWithParams}`;
    loadingService.requestStarted(requestId, requestLabel);

    return next(request).pipe(finalize(() => loadingService.requestFinished(requestId)));
};

function isBackendRequest(url: string): boolean {
    const normalizedBaseApiUrl = (environment.baseApiURL || '').replace(/\/+$/, '');
    const normalizedRequestUrl = (url || '').replace(/\/+$/, '');

    if (!normalizedBaseApiUrl) {
        return normalizedRequestUrl.includes('/api/');
    }

    return normalizedRequestUrl.startsWith(normalizedBaseApiUrl);
}

function buildRequestId(request: HttpRequest<unknown>): string {
    return `${request.method}-${request.urlWithParams}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
